import descriptionIcon from "@assets/images/description.svg";
import documentAddIcon from "@assets/images/document-add.svg";
import excelUploadIcon from "@assets/images/excel-upload.svg";
import externalLinkIcon from "@assets/images/external-link-currentColor.svg";
import inquiryIcon from "@assets/images/inquiry.svg";
import magnifierIcon from "@assets/images/magnifier.svg";
import maintainIcon from "@assets/images/maintain.svg";
// import notificationBellIcon from "@assets/images/noti-bell.svg";
import clipboard from "@assets/images/saleor-list.svg";
import suitCaseIcon from "@assets/images/suit-case.svg";
import uploadIcon from "@assets/images/upload.svg";
import { alternatedMaterialUrl } from "@saleor/alternatedMaterial/urls";
import {
  extensionMountPoints,
  useExtensions
} from "@saleor/apps/useExtensions";
import { useUser } from "@saleor/auth";
import { changeOrderUrl } from "@saleor/changeOrder/urls";
import { createOrderNotRefUrl } from "@saleor/createOrChangeNotRefContract/urls";
import { createOrderUrl } from "@saleor/createOrder/urls";
import { customerMaterialUrl } from "@saleor/customerMaterial/urls";
import { deliveryReport } from "@saleor/deliveryReport/urls";
import { excelUploadUrl } from "@saleor/excelUpload/urls";
import {
  PermissionEnum,
  // useRequireAttentionItemsQuery,
  UserFragment
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { inquirySearchOrderUrl } from "@saleor/inquirySearchOrder/urls";
import { homepageSectionNames, sectionNames } from "@saleor/intl";
import {
  listOfSaleOrderTrackingUrl,
  listOfSaleOrderUrl
} from "@saleor/listOfSalesOrder/urls";
import { SidebarMenuItem } from "@saleor/macaw-ui";
import { orderConfirmUrl } from "@saleor/OrderConfirm/urls";
import {
  pendingOrderTrackingUrl,
  pendingOrderUrl
} from "@saleor/PendingOrder/urls";
import { useDerivedPermissions } from "@saleor/permissionHooks";
import { poUploadUrl } from "@saleor/poUpload/urls";
// import { requiredAttentionSection } from "@saleor/requiredAttention/urls";
import { stockOnHandUrl } from "@saleor/stockOnHand/url";
import { userManagementSection } from "@saleor/userManagement/urls";
import { useMemo } from "react";
import { IntlShape } from "react-intl";
import useRouter from "use-react-router";

import { saveDraftUrl } from "./../../saveDraft/urls";
import { getMenuItemExtension } from "./utils";

export enum PermissionGroup {
  MAINTAIN = "Maintain",
  CS_MAINTAIN = "CS Maintain"
}

interface SidebarMenuItemExtend extends SidebarMenuItem {
  homepageLabel?: string;
  subLabel?: string;
}

export interface FilterableMenuItem
  extends Omit<SidebarMenuItemExtend, "children"> {
  children?: FilterableMenuItem[];
  permissions?: PermissionEnum[];
  showPoUpload?: boolean;
  permissionGroups?: PermissionGroup[];
  isRedirectLink?: boolean;
  matchAll?: boolean;
}

function useMenuStructure(
  intl: IntlShape,
  user: UserFragment
): [
  SidebarMenuItemExtend[],
  (menuItem: SidebarMenuItemExtend) => void
  // () => void
] {
  const navigate = useNavigator();
  const { location } = useRouter();
  const { zone } = useUser();
  const extensions = useExtensions(extensionMountPoints.NAVIGATION_SIDEBAR);
  const [{ showPoUpload }] = useAppState();

  const handleMenuItemClick = (menuItem: SidebarMenuItemExtend) => {
    const extension = getMenuItemExtension(extensions, menuItem);
    if (extension) {
      extension.open();
      return;
    }
    navigate(menuItem.url, { resetScroll: true });
  };

  // const queryVariablesDataAttention = useMemo(
  //   () => ({
  //     first: 20,
  //     filter: {},
  //     role: zone
  //   }),
  //   [zone, location]
  // );

  // const {
  //   data: dataAttention,
  //   refetch: refetchRequireAttentionNotify
  // } = useRequireAttentionItemsQuery({
  //   variables: queryVariablesDataAttention,
  //   skip: !zone
  // });

  const {
    pendingOrderPermission,
    altMatPermission,
    poUploadPermission,
    deliveryPermission,
    orderConfirmPermission,
    pendingOrderTrackingPermission,
    notRefCreateOrderTrackingPermission,
    listOfSaleOrderTrackingPermission,
    excelUploadPermission
  } = useDerivedPermissions();

  const isCustomer = useMemo(
    () =>
      // if (
      //   // user?.userPermissions?.find(
      //   //   e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      //   // )
      // ) {
      //   return false;
      // }
      user?.userPermissions?.find(
        e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      ),
    [user]
  );

  const isHomePage = location.pathname === "/";
  const isNotRef = useMemo(
    () =>
      user?.userPermissions?.find(
        e =>
          e.code === PermissionEnum.CREATE_ORDER_DOMESTIC_PACKAGING ||
          e.code === PermissionEnum.CREATE_ORDER_DOMESTIC
      ),
    [user]
  );
  // to hide the whole menu if the children is empty
  const isUserCanMaintain = useMemo(() => {
    const MAINTAIN_PERMISSIONS = [
      PermissionEnum.MAINTAIN_EMAIL,
      PermissionEnum.MAINTAIN_USER,
      PermissionEnum.MAINTAIN_ALT_MATERIAL,
      PermissionEnum.MAINTAIN_CUSTOMER_MATERIAL
    ];
    return user?.userPermissions?.some(e =>
      MAINTAIN_PERMISSIONS.includes(e.code)
    );
  }, [user]);

  const menuItems: FilterableMenuItem[] = useMemo(
    () => [
      {
        ariaLabel: "createOrder",
        iconSrc: user?.isStaff ? documentAddIcon : suitCaseIcon,
        label: intl.formatMessage(
          user?.isStaff
            ? sectionNames.createOrder
            : sectionNames.createOrderCustomer
        ),
        homepageLabel: intl.formatMessage(
          user?.isStaff
            ? sectionNames.createOrder
            : sectionNames.createOrderCustomer
        ),
        id: "createOrder",
        url: createOrderUrl,
        permissions: [
          PermissionEnum.CREATE_ORDER_CUSTOMER,
          PermissionEnum.CREATE_ORDER_DOMESTIC,
          PermissionEnum.CREATE_ORDER_EXPORT
        ],
        children:
          isNotRef && !isHomePage
            ? [
                {
                  ariaLabel: "createOrder",
                  homepageLabel: intl.formatMessage(sectionNames.refContract),
                  label: intl.formatMessage(sectionNames.refContract),
                  id: "createOrder",
                  url: createOrderUrl,
                  permissions: [
                    PermissionEnum.CREATE_ORDER_CUSTOMER,
                    PermissionEnum.CREATE_ORDER_DOMESTIC,
                    PermissionEnum.CREATE_ORDER_EXPORT
                  ]
                },
                {
                  url: createOrderNotRefUrl,
                  id: "createOrderNotRef",
                  label: intl.formatMessage(sectionNames.notRefContract),
                  permissions: notRefCreateOrderTrackingPermission
                }
              ]
            : null
      },
      {
        ariaLabel: "createOrderNotRef",
        iconSrc: user?.isStaff ? documentAddIcon : suitCaseIcon,
        homepageLabel: intl.formatMessage(sectionNames.notRefContract),
        label: "Create Order",
        subLabel: "Packaging",
        id: "createOrderNotRef",
        url: createOrderNotRefUrl,
        permissions: notRefCreateOrderTrackingPermission
      },
      {
        ariaLabel: "changeOrder",
        iconSrc: magnifierIcon,
        label: intl.formatMessage(sectionNames.changeOrder),
        homepageLabel: intl.formatMessage(sectionNames.changeOrder),
        id: "changeOrder",
        url: changeOrderUrl,
        permissions: [
          PermissionEnum.VIEW_ORDER_DOMESTIC,
          PermissionEnum.VIEW_ORDER_EXPORT,
          PermissionEnum.CHANGE_ORDER_DOMESTIC,
          PermissionEnum.CHANGE_ORDER_EXPORT
          // PermissionEnum.CHANGE_ORDER_DOMESTIC_NRC,
          // PermissionEnum.VIEW_ORDER_DOMESTIC_NRC,
          // PermissionEnum.CREATE_ORDER_DOMESTIC_PACKAGING
          // PermissionEnum.VIEW_ORDER_CUSTOMER,
          // PermissionEnum.CHANGE_ORDER_CUSTOMER,
        ]
      },
      {
        ariaLabel: "listOfSaleOrder",
        iconSrc: clipboard,
        label: intl.formatMessage(sectionNames.listOfSaleOrder),
        homepageLabel: intl.formatMessage(sectionNames.listOfSaleOrder),
        id: "listOfSaleOrder",
        url: listOfSaleOrderUrl,
        permissions: [
          PermissionEnum.VIEW_STOCK,
          PermissionEnum.ALL_REPORT_DOMESTIC,
          // PermissionEnum.VIEW_ORDER_DOMESTIC,
          PermissionEnum.ALL_REPORT_EXPORT,
          // PermissionEnum.ALL_REPORT_DOMESTIC_NRC
          // PermissionEnum.VIEW_ORDER_EXPORT
          ...listOfSaleOrderTrackingPermission
        ],
        children: [
          {
            url: listOfSaleOrderUrl,
            id: "listOfSaleOrder",
            label: intl.formatMessage(sectionNames.listOfSaleOrder),
            permissions: [
              PermissionEnum.VIEW_STOCK,
              PermissionEnum.ALL_REPORT_DOMESTIC,
              PermissionEnum.ALL_REPORT_EXPORT
            ]
          },
          {
            url: listOfSaleOrderTrackingUrl,
            id: "listOfSaleOrderTracking",
            label: intl.formatMessage(sectionNames.listOfSaleOrderTracking),
            permissions: listOfSaleOrderTrackingPermission
          }
        ]
      },
      // {
      //   ariaLabel: "requiredAttention",
      //   iconSrc: notificationBellIcon,
      //   label: intl.formatMessage(sectionNames.requiredAttention),
      //   homepageLabel: intl.formatMessage(sectionNames.requiredAttention),
      //   id: "requiredAttention",
      //   url: requiredAttentionSection,
      //   permissions: [
      //     // PermissionEnum.VIEW_ORDER_CUSTOMER,
      //     PermissionEnum.VIEW_ORDER_DOMESTIC,
      //     PermissionEnum.VIEW_ORDER_EXPORT
      //   ],
      //   notifyChip: dataAttention?.requireAttentionItems?.totalCount
      // },
      {
        ariaLabel: "poUpload",
        iconSrc: uploadIcon,
        label: intl.formatMessage(sectionNames.poUpload),
        homepageLabel: intl.formatMessage(sectionNames.poUpload),
        id: "poUpload",
        url: poUploadUrl,
        permissions: poUploadPermission,
        showPoUpload
      },
      {
        ariaLabel: "excelUpload",
        iconSrc: excelUploadIcon,
        label: intl.formatMessage(sectionNames.excelUpload),
        homepageLabel: intl.formatMessage(sectionNames.excelUpload),
        id: "excelUpload",
        url: excelUploadUrl,
        permissions: excelUploadPermission
      },
      {
        ariaLabel: "inquiry",
        iconSrc: inquiryIcon,
        label: intl.formatMessage(sectionNames.inquiry),
        homepageLabel: intl.formatMessage(sectionNames.inquiry),
        id: "inquiry",
        permissions: [
          PermissionEnum.ALL_REPORT_ADMIN,
          PermissionEnum.ALL_REPORT_CUSTOMER,
          PermissionEnum.VIEW_STOCK,
          ...pendingOrderTrackingPermission,
          ...orderConfirmPermission
        ],
        children:
          isCustomer && !user?.isSuperuser
            ? [
                {
                  url: orderConfirmUrl,
                  id: "orderconfirm",
                  permissions: [
                    PermissionEnum.ALL_REPORT_CUSTOMER,
                    PermissionEnum.VIEW_ORDER_CONFIRM_REPORT
                  ],
                  label: intl.formatMessage(sectionNames.orderConfirm)
                },
                {
                  url: inquirySearchOrderUrl,
                  id: "inquiry1",
                  permissions: [PermissionEnum.VIEW_ORDER_CUSTOMER],
                  label: intl.formatMessage(sectionNames.inquiry2)
                },
                {
                  url: pendingOrderUrl,
                  id: "pendingOrder",
                  label: intl.formatMessage(sectionNames.inquiry3)
                },
                {
                  url: deliveryReport,
                  id: "DeliveryReportMain",
                  permissions: [PermissionEnum.ALL_REPORT_CUSTOMER],
                  label: intl.formatMessage(sectionNames.inquiry4)
                },
                {
                  url: process.env.KOS_URL,
                  id: "inquiry4",
                  isRedirectLink: true,
                  label: intl.formatMessage(sectionNames.inquiry5)
                }
              ]
            : [
                {
                  url: pendingOrderUrl,
                  id: "inquiry1",
                  permissions: pendingOrderPermission,
                  label: intl.formatMessage(sectionNames.inquiry1Admin)
                },
                {
                  url: pendingOrderTrackingUrl,
                  id: "pendingOrderTracking",
                  permissions: pendingOrderTrackingPermission,
                  label: intl.formatMessage(sectionNames.pendingOrderTracking)
                },
                {
                  url: orderConfirmUrl,
                  id: "orderconfirm",
                  permissions: orderConfirmPermission,
                  label: intl.formatMessage(sectionNames.orderConfirmAdmin)
                },
                {
                  url: deliveryReport,
                  id: "DeliveryReportMain",
                  permissions: deliveryPermission,
                  label: intl.formatMessage(sectionNames.inquiry3Admin)
                },
                {
                  url: process.env.KOS_URL,
                  id: "inquiry3",
                  isRedirectLink: true,
                  label: intl.formatMessage(sectionNames.inquiry4Admin),
                  permissions: [
                    PermissionEnum.ALL_REPORT_ADMIN,
                    PermissionEnum.ALL_REPORT_EXPORT
                  ]
                },
                {
                  url: stockOnHandUrl,
                  id: "inquiry4",
                  label: intl.formatMessage(sectionNames.inquiry5Admin),
                  permissions: [
                    PermissionEnum.ALL_REPORT_ADMIN,
                    PermissionEnum.ALL_REPORT_EXPORT
                  ]
                }
              ]
      },
      {
        ariaLabel: "iplanReport",
        iconSrc: externalLinkIcon,
        label: intl.formatMessage(sectionNames.iplanReport),
        homepageLabel: intl.formatMessage(homepageSectionNames.iplanReport),
        id: "iplanReport",
        permissions: [
          PermissionEnum.VIEW_STOCK,
          PermissionEnum.ALL_REPORT_ADMIN
        ],
        children: [
          {
            url: process.env.IPLAN_STOCK_URL,
            id: "iplanReport1",
            isRedirectLink: true,
            label: intl.formatMessage(sectionNames.iplanReport1),
            permissions: [PermissionEnum.VIEW_STOCK]
          },
          {
            url: process.env.IPLAN_ORDER_URL,
            id: "iplanReport2",
            isRedirectLink: true,
            label: intl.formatMessage(sectionNames.iplanReport2),
            permissions: [PermissionEnum.ALL_REPORT_ADMIN]
          }
        ]
      },

      // {
      //   ariaLabel: "userManagement",
      //   iconSrc: usersIcon,
      //   // homepageLabel: intl.formatMessage(sectionNames.userManagement),
      //   label: intl.formatMessage(sectionNames.userManagement),
      //   id: "userManagement",
      //   url: userManagementSection,
      //   permissions: [PermissionEnum.MAINTAIN_USER]
      // },
      {
        ariaLabel: "saveDraft",
        iconSrc: descriptionIcon,
        label: intl.formatMessage(sectionNames.saveDraft),
        id: "saveDraft",
        url: saveDraftUrl,
        permissions: [PermissionEnum.VIEW_DRAFT_ORDER]
      },
      isUserCanMaintain && {
        ariaLabel: "maintain",
        iconSrc: maintainIcon,
        label: intl.formatMessage(sectionNames.maintain),
        homepageLabel: intl.formatMessage(sectionNames.maintain),
        id: "maintain",
        permissions: [
          PermissionEnum.MAINTAIN_USER,
          PermissionEnum.MAINTAIN_USER_GROUP,
          PermissionEnum.MAINTAIN_EMAIL,
          PermissionEnum.MANAGE_STAFF,
          PermissionEnum.MAINTAIN_EMAIL_EO_UPLOAD,
          PermissionEnum.MAINTAIN_ORGANIZATION,
          PermissionEnum.MAINTAIN_PO_UPLOAD,
          PermissionEnum.MAINTAIN_ROLE,
          PermissionEnum.MAINTAIN_ROLE_PERMISSION,
          PermissionEnum.MAINTAIN_ALT_MATERIAL,
          PermissionEnum.MAINTAIN_CUSTOMER_MATERIAL
        ],
        children: [
          {
            url: alternatedMaterialUrl,
            id: "alternateMaterialOS",
            permissions: altMatPermission,
            label: intl.formatMessage(sectionNames.maintainMenu1)
          },
          {
            url: customerMaterialUrl,
            id: "customerMaterial",
            permissions: [PermissionEnum.MAINTAIN_CUSTOMER_MATERIAL],
            label: intl.formatMessage(sectionNames.customerMaterial)
          },
          {
            ariaLabel: "userManagement",
            // iconSrc: usersIcon,
            homepageLabel: intl.formatMessage(sectionNames.userManagement),
            label: intl.formatMessage(sectionNames.userManagement),
            id: "userManagement",
            url: userManagementSection,
            permissions: [PermissionEnum.MAINTAIN_USER]
          },
          {
            // url: "#",
            id: "emailConfiguration",
            label: intl.formatMessage(sectionNames.maintainMenu3),
            permissions: [PermissionEnum.MAINTAIN_EMAIL]
          }
        ]
      }
    ],
    [
      poUploadPermission,
      isCustomer,
      // dataAttention?.requireAttentionItems?.totalCount,
      showPoUpload,
      isNotRef,
      isHomePage
    ]
  );

  const isMenuItemPermitted = (menuItem: FilterableMenuItem) => {
    if (!menuItem?.permissions || (user?.isSuperuser && zone === "Domestic")) {
      return true;
    }

    const userPermissions = (user?.userPermissions || []).map(
      permission => permission.code
    );

    if (
      menuItem.showPoUpload &&
      userPermissions.includes(PermissionEnum.ALL_UPLOAD_PO_CUSTOMER)
    ) {
      return true;
    }

    if (menuItem.matchAll && menuItem?.permissions) {
      return menuItem.permissions.every(permission =>
        userPermissions.includes(permission)
      );
    }

    if (menuItem?.permissions) {
      return menuItem.permissions.some(permission =>
        userPermissions.includes(permission)
      );
    }
  };

  const getFilteredMenuItems = (menuItems: FilterableMenuItem[]) =>
    menuItems.filter(isMenuItemPermitted);

  return [
    menuItems.reduce(
      (resultItems: FilterableMenuItem[], menuItem: FilterableMenuItem) => {
        if (!isMenuItemPermitted(menuItem)) {
          return resultItems;
        }
        const { children } = menuItem;
        const filteredChildren = children
          ? getFilteredMenuItems(children)
          : undefined;
        return [...resultItems, { ...menuItem, children: filteredChildren }];
      },
      [] as FilterableMenuItem[]
    ),
    handleMenuItemClick
    // refetchRequireAttentionNotify
  ];
}

export default useMenuStructure;
